import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, Save, ChevronLeft, UserPlus } from 'lucide-react';
import { achatsApi } from '@/api/achats';
import { useCreerBca, useModifierBca, useFournisseurs, useMesBcas } from '@/hooks/useAchats';
import { NouveauFournisseurDialog } from './NouveauFournisseurDialog';

/**
 * SIMG-ACHATS-LEADBETON-2026-002 — Créer OU modifier un BCA.
 *
 * Mode édition activé via ?edit=<uuid>. Charge le BCA existant depuis le cache
 * `useMesBcas()` (même endpoint que MesBcasPage — évite un fetch supplémentaire
 * pour un seul BCA). Le formulaire est pré-rempli et le bouton Enregistrer
 * fait un PATCH au lieu d'un POST.
 */

type Nature = 'matiere_premiere' | 'fourniture' | 'service' | 'immobilisation';

interface LigneBrouillon {
  /** 11/10/2026 : une ligne est une matière du référentiel, ou un article libre (fourniture, service, matériel). */
  nature:            Nature;
  matierePremiereId?: string;
  designation?:      string;
  categorie?:        string;
  quantite:          number;
  prixUnitaire:      number;
}

const NATURES: { value: Exclude<Nature, 'matiere_premiere'>; libelle: string; aide: string }[] = [
  { value: 'fourniture',     libelle: 'Fourniture',           aide: 'consommée tout de suite : essence, huile, petit outillage' },
  { value: 'service',        libelle: 'Service',              aide: 'prestation, transport, réparation' },
  { value: 'immobilisation', libelle: 'Matériel (patrimoine)', aide: 'bien durable : bétonnière, vibreur, véhicule' },
];
const CATEGORIES = [
  { value: 'materiel_outillage',    libelle: 'Matériel et outillage' },
  { value: 'materiel_transport',    libelle: 'Matériel de transport' },
  { value: 'materiel_mobilier',     libelle: 'Mobilier' },
  { value: 'materiel_informatique', libelle: 'Informatique' },
  { value: 'agencements',           libelle: 'Agencements' },
  { value: 'autre',                 libelle: 'Autre' },
];
const champ = { padding: '10px 12px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 16, width: '100%', boxSizing: 'border-box' as const, background: '#fff' };

export const NouveauBcaPage = () => {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get('edit');

  const [fournisseurId, setF]     = useState('');
  const [dateLivraison, setDate]  = useState('');
  const [notes, setNotes]         = useState('');
  const [lignes, setLignes]       = useState<LigneBrouillon[]>([]);
  const [showMp, setShowMp]       = useState(false);
  const [showAutre, setShowAutre] = useState(false);
  const [autreNature, setAutreNature] = useState<Exclude<Nature, 'matiere_premiere'>>('fourniture');
  const [autreDesignation, setAutreDesignation] = useState('');
  const [autreCategorie, setAutreCategorie] = useState('materiel_outillage');
  const [prefilled, setPrefilled] = useState(false);
  const [showNewFournisseur, setShowNewF] = useState(false);

  const creer     = useCreerBca();
  const modifier  = useModifierBca();
  const { data: fournisseurs = [] } = useFournisseurs();
  const { data: mesBcas = [] } = useMesBcas();
  const bcaEdit = editId ? mesBcas.find((b) => b.id === editId) : undefined;

  const mpsQ = useQuery({
    queryKey: ['achats', 'mp'],
    queryFn:  () => achatsApi.matieresPremieres(),
    staleTime: 5 * 60_000,
  });

  const mpsById = useMemo(() => {
    const m = new Map<string, any>();
    (mpsQ.data ?? []).forEach((mp) => m.set(mp.id, mp));
    return m;
  }, [mpsQ.data]);

  // Pré-remplissage en mode édition — une seule fois, quand le BCA cible est disponible.
  useEffect(() => {
    if (!editId || prefilled || !bcaEdit) return;
    setF(bcaEdit.fournisseurId);
    setDate(bcaEdit.dateLivraisonPrevue ? bcaEdit.dateLivraisonPrevue.slice(0, 10) : '');
    setNotes(bcaEdit.notes ?? '');
    setLignes(
      (bcaEdit.lignes ?? []).map((l) => ({
        nature:            (l.nature ?? 'matiere_premiere') as Nature,
        matierePremiereId: l.matierePremiereId ?? undefined,
        designation:       l.designation ?? undefined,
        categorie:         l.categorieImmobilisation ?? undefined,
        quantite:          Number(l.quantite),
        prixUnitaire:      Number(BigInt(l.prixUnitaireCents.toString())) / 100,
      })),
    );
    setPrefilled(true);
  }, [editId, prefilled, bcaEdit]);

  const totalHt = useMemo(
    () => lignes.reduce((s, l) => s + Math.round(l.quantite * l.prixUnitaire), 0),
    [lignes],
  );
  const totalTtc = Math.round(totalHt * 1.18);

  const missings: string[] = [];
  if (!fournisseurId) missings.push('Fournisseur');
  if (lignes.length === 0) missings.push('Au moins une ligne');
  if (lignes.some((l) => !l.quantite || l.quantite <= 0)) missings.push('Quantités > 0');
  if (lignes.some((l) => !l.prixUnitaire || l.prixUnitaire <= 0)) missings.push('Prix unitaires > 0');
  if (lignes.some((l) => l.nature !== 'matiere_premiere' && !l.designation?.trim())) missings.push('Désignation des articles');
  const canSubmit = missings.length === 0;

  const enregistrer = async () => {
    if (!canSubmit) return;
    const payload = {
      fournisseurId,
      dateLivraisonPrevue: dateLivraison || undefined,
      notes: notes.trim() || undefined,
      lignes: lignes.map((l) => (l.nature === 'matiere_premiere'
        ? { matierePremiereId: l.matierePremiereId, quantite: Number(l.quantite), prixUnitaireFcfa: Number(l.prixUnitaire) }
        : {
            nature:            l.nature,
            designation:       l.designation?.trim(),
            categorieImmobilisation: l.nature === 'immobilisation' ? (l.categorie ?? 'autre') : undefined,
            quantite:          Number(l.quantite),
            prixUnitaireFcfa:  Number(l.prixUnitaire),
          })),
    };
    const bca = editId
      ? await modifier.mutateAsync({ id: editId, body: payload })
      : await creer.mutateAsync(payload);
    nav(`/achats/bca?highlight=${bca.id}`);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => nav(-1)}
          aria-label="Retour"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--tx-2, #374151)' }}
        >
          <ChevronLeft size={20} />
        </button>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>
          {editId ? `Modifier ${bcaEdit?.reference ?? 'le BCA'}` : 'Nouveau bon de commande'}
        </h1>
      </div>

      <Section titre="Fournisseur">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            value={fournisseurId} onChange={(e) => setF(e.target.value)}
            style={{ flex: '1 1 200px', minWidth: 0, padding: '10px 12px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 16, background: '#fff' }}
          >
            <option value="">— Choisir un fournisseur —</option>
            {fournisseurs.map((f) => (
              <option key={f.id} value={f.id}>{f.code} · {f.raisonSociale} ({f.categorie})</option>
            ))}
          </select>
          <button
            onClick={() => setShowNewF(true)}
            title="Créer un nouveau fournisseur"
            style={{
              padding: '10px 12px', borderRadius: 8, border: '1px dashed #0F766E',
              background: '#F0FDFA', color: '#0F766E', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <UserPlus size={14} /> Nouveau
          </button>
        </div>
      </Section>

      {showNewFournisseur && (
        <NouveauFournisseurDialog
          onClose={() => setShowNewF(false)}
          onCreated={(id) => { setF(id); setShowNewF(false); }}
        />
      )}

      <Section titre="Articles commandés">
        {lignes.map((l, i) => {
          const mp = l.matierePremiereId ? mpsById.get(l.matierePremiereId) : undefined;
          const total = Math.round(l.quantite * l.prixUnitaire);
          return (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'start',
              padding: 10, background: 'var(--bg-1, #FAFAFA)', borderRadius: 8, marginBottom: 8,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, overflowWrap: 'anywhere' }}>
                  {l.nature === 'matiere_premiere' ? `${mp?.code ?? '?'} · ${mp?.nom ?? ''}` : (l.designation || 'Article à préciser')}
                </div>
                {l.nature !== 'matiere_premiere' && (
                  <div style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)', marginTop: 2 }}>
                    {NATURES.find((n) => n.value === l.nature)?.libelle}
                    {l.nature === 'immobilisation' && ` · ${CATEGORIES.find((c) => c.value === (l.categorie ?? 'autre'))?.libelle ?? ''}`}
                  </div>
                )}
                {/* Deux colonnes égales, libellé AU-DESSUS du champ : sur un téléphone, la grille à largeurs fixes
                    (90 px + 120 px + libellés) poussait le prix unitaire hors de l'écran. */}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8, marginTop: 8 }}>
                  <label style={{ display: 'block', minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)', marginBottom: 2 }}>Quantité{mp?.unite ? ` (${mp.unite})` : ''}</div>
                    <input
                      type="number" inputMode="decimal" min={0.001} step="0.001" value={l.quantite}
                      onChange={(e) => setLignes(lignes.map((x, j) => j === i ? { ...x, quantite: Number(e.target.value) } : x))}
                      style={champ}
                    />
                  </label>
                  <label style={{ display: 'block', minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)', marginBottom: 2 }}>Prix unitaire (FCFA)</div>
                    <input
                      type="number" inputMode="numeric" min={1} value={l.prixUnitaire}
                      onChange={(e) => setLignes(lignes.map((x, j) => j === i ? { ...x, prixUnitaire: Number(e.target.value) } : x))}
                      style={champ}
                    />
                  </label>
                </div>
                <div style={{ marginTop: 6, fontSize: 13, fontFamily: 'monospace', fontWeight: 700 }}>
                  {total.toLocaleString('fr-FR')} FCFA
                </div>
              </div>
              <button
                onClick={() => setLignes(lignes.filter((_, j) => j !== i))}
                aria-label="Supprimer la ligne"
                style={{ background: 'transparent', border: 'none', color: '#B91C1C', cursor: 'pointer', padding: 8 }}
              >
                <Trash2 size={18} />
              </button>
            </div>
          );
        })}

        {!showMp ? (
          <button
            onClick={() => setShowMp(true)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', padding: '12px', background: 'var(--bg-1, #FAFAFA)',
              border: '2px dashed var(--bd-2, #D1D5DB)', borderRadius: 8,
              cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'var(--tx-2, #374151)',
            }}
          >
            <Plus size={16} /> Ajouter une matière première
          </button>
        ) : (
          <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--bd-1)', borderRadius: 8, background: 'var(--bg-1)' }}>
            {(mpsQ.data ?? []).map((mp) => (
              <button
                key={mp.id}
                onClick={() => {
                  setLignes([...lignes, {
                    nature:            'matiere_premiere',
                    matierePremiereId: mp.id,
                    quantite:          1,
                    prixUnitaire:      Number(BigInt(mp.prixUnitaireCents.toString())) / 100,
                  }]);
                  setShowMp(false);
                }}
                style={{
                  width: '100%', textAlign: 'left', padding: '10px 12px',
                  background: 'transparent', border: 'none', borderBottom: '1px solid var(--bd-1)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{mp.code} · {mp.nom}</div>
                <div style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)' }}>
                  ~{(Number(BigInt(mp.prixUnitaireCents.toString())) / 100).toLocaleString('fr-FR')} FCFA / {mp.unite}
                  {mp.fournisseurNom && ` · ${mp.fournisseurNom}`}
                </div>
              </button>
            ))}
          </div>
        )}

        {!showAutre ? (
          <button
            onClick={() => { setShowAutre(true); setShowMp(false); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', padding: '12px', marginTop: 8, background: 'var(--bg-1, #FAFAFA)',
              border: '2px dashed var(--bd-2, #D1D5DB)', borderRadius: 8,
              cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'var(--tx-2, #374151)',
            }}
          >
            <Plus size={16} /> Autre achat : fourniture, service, matériel
          </button>
        ) : (
          <div style={{ marginTop: 8, padding: 12, border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 8, background: 'var(--bg-1, #FAFAFA)', display: 'grid', gap: 10 }}>
            <Field label="Type d'achat">
              <select value={autreNature} onChange={(e) => setAutreNature(e.target.value as Exclude<Nature, 'matiere_premiere'>)} style={champ}>
                {NATURES.map((n) => <option key={n.value} value={n.value}>{n.libelle}</option>)}
              </select>
              <div style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)', marginTop: 4 }}>{NATURES.find((n) => n.value === autreNature)?.aide}</div>
            </Field>
            <Field label="Ce qui est acheté">
              <input value={autreDesignation} onChange={(e) => setAutreDesignation(e.target.value)}
                     placeholder="ex. Essence du vibreur, Brouette de chantier" style={champ} />
            </Field>
            {autreNature === 'immobilisation' && (
              <Field label="Catégorie du matériel">
                <select value={autreCategorie} onChange={(e) => setAutreCategorie(e.target.value)} style={champ}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.libelle}</option>)}
                </select>
              </Field>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8 }}>
              <button onClick={() => { setShowAutre(false); setAutreDesignation(''); }}
                      style={{ padding: 12, borderRadius: 8, border: '1px solid var(--bd-2, #D1D5DB)', background: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                Annuler
              </button>
              <button
                disabled={autreDesignation.trim().length < 2}
                onClick={() => {
                  setLignes([...lignes, {
                    nature: autreNature, designation: autreDesignation.trim(),
                    categorie: autreNature === 'immobilisation' ? autreCategorie : undefined,
                    quantite: 1, prixUnitaire: 0,
                  }]);
                  setShowAutre(false); setAutreDesignation('');
                }}
                style={{ padding: 12, borderRadius: 8, border: 'none', color: '#fff', fontWeight: 700,
                         background: autreDesignation.trim().length < 2 ? '#D1D5DB' : '#0F766E',
                         cursor: autreDesignation.trim().length < 2 ? 'not-allowed' : 'pointer' }}>
                Ajouter
              </button>
            </div>
          </div>
        )}
      </Section>

      <Section titre="Options">
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Date livraison prévue">
            <input
              type="date" value={dateLivraison} onChange={(e) => setDate(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 14, width: '100%' }}
            />
          </Field>
          <Field label="Notes">
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Ref devis fournisseur, conditions particulières…"
              style={{ padding: '8px 12px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 14, width: '100%', resize: 'vertical' }}
            />
          </Field>
        </div>
      </Section>

      <div style={{ background: '#fff', border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <Row label="Total HT" value={totalHt.toLocaleString('fr-FR') + ' FCFA'} bold />
        <Row label="TVA 18%" value={(totalTtc - totalHt).toLocaleString('fr-FR') + ' FCFA'} muted />
        <Row label="Total TTC" value={totalTtc.toLocaleString('fr-FR') + ' FCFA'} bold big />
      </div>

      {!canSubmit && (
        <div style={{ marginBottom: 12, fontSize: 13, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px' }}>
          À compléter : <strong>{missings.join(', ')}</strong>
        </div>
      )}

      <button
        onClick={enregistrer}
        disabled={!canSubmit || creer.isPending || modifier.isPending}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          width: '100%', padding: '14px',
          background: canSubmit ? '#0F766E' : '#D1D5DB',
          color: '#fff', border: 'none', borderRadius: 12,
          fontWeight: 700, fontSize: 15,
          cursor: canSubmit ? 'pointer' : 'not-allowed',
        }}
      >
        <Save size={18} />
        {creer.isPending || modifier.isPending
          ? 'Enregistrement…'
          : editId ? 'Enregistrer les modifications' : 'Enregistrer le BCA'}
      </button>
    </div>
  );
};

function Section({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--bd-1)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>{titre}</div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}

function Row({ label, value, bold, muted, big }: { label: string; value: string; bold?: boolean; muted?: boolean; big?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '4px 0', fontSize: big ? 16 : 14,
      fontWeight: bold ? 700 : 400,
      color: muted ? 'var(--tx-3, #6B7280)' : 'var(--tx-1, #111827)',
    }}>
      <span>{label}</span>
      <span style={{ fontFamily: 'monospace' }}>{value}</span>
    </div>
  );
}

// La modale NouveauFournisseurDialog vit dans son propre fichier — reutilisee
// aussi par FournisseursPage (repertoire).
